import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, PackageSearch, LogOut, LineChart, CalendarRange, GitCompare, Archive, Sparkles,
} from 'lucide-react';
import CostTrackerWidget from '@/components/CostTrackerWidget';
import AskAI from '@/components/AskAI';
import { useAuth } from '@/auth/AuthContext';

const NAV_ITEMS = [
  { to: '/dashboard',             label: 'Dashboard',                    icon: LayoutDashboard, testid: 'nav-dashboard' },
  { to: '/allocation',            label: 'Stock Allocation',             icon: PackageSearch,   testid: 'nav-allocation' },
  { to: '/projections',           label: 'Projections vs Consumption',   icon: LineChart,       testid: 'nav-projections' },
  { to: '/rolling-projections',   label: 'Monthly Rolling Projections',  icon: CalendarRange,   testid: 'nav-rolling-projections' },
  { to: '/smart-projections',     label: 'Smart Projections',            icon: Sparkles,        testid: 'nav-smart-projections' },
  { to: '/inventory-recon',       label: 'Inventory Recon',              icon: GitCompare,      testid: 'nav-inventory-recon' },
  { to: '/historical-snapshots',  label: 'Historical Snapshots',         icon: Archive,         testid: 'nav-historical-snapshots' },
];

export default function AppShell({ children }) {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  // Server-side session revocation + clear local token + return to LoginModal.
  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-background">
      <aside
        className="hidden md:flex md:w-60 lg:w-64 flex-col border-r border-border bg-card sticky top-0 h-screen"
        data-testid="app-sidebar"
      >
        <div className="px-5 py-6 border-b border-border">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Wellversed</p>
          <p className="text-base font-semibold text-wv-ink mt-1 leading-tight">RADAR</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV_ITEMS.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-wv-body hover:bg-muted hover:text-wv-ink'
                }`
              }
            >
              <Icon size={16} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-border space-y-2">
          <div className="flex items-center justify-end px-1" data-testid="cost-tracker-slot">
            <CostTrackerWidget />
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-wv-body hover:bg-muted hover:text-wv-ink transition-colors"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile top-nav fallback: compact horizontal tabs */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 flex items-center gap-1 overflow-x-auto bg-card border-b border-border px-3 py-2">
        {NAV_ITEMS.map(({ to, label, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`${testid}-mobile`}
            className={({ isActive }) =>
              `shrink-0 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${
                isActive ? 'bg-primary/10 text-primary' : 'text-wv-body hover:bg-muted'
              }`
            }
          >
            {label}
          </NavLink>
        ))}
        <div className="ml-auto flex items-center gap-1">
          <CostTrackerWidget />
          <button
            onClick={handleLogout}
            data-testid="logout-button-mobile"
            className="shrink-0 px-3 py-1.5 rounded-md text-xs font-medium text-wv-body hover:bg-muted"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="flex-1 min-w-0 md:pl-0 pt-12 md:pt-0">{children}</div>

      {/* Global AI Data Assistant — floating trigger + slide-over chat. */}
      <AskAI />
    </div>
  );
}
